import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { marketApi } from "@/lib/marketApi";

const POLL_MS = 5000;

export default function MarketChatPanel({ thread, onBack, onThreadUpdate }) {
  const auth = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const lastFetchedRef = useRef(null);

  const refresh = async (initial = false) => {
    try {
      const r = await marketApi.messages(auth.token, thread.id, initial ? null : lastFetchedRef.current);
      if (initial) {
        setMsgs(r.messages);
      } else if (r.messages.length) {
        setMsgs((prev) => [...prev, ...r.messages]);
      }
      if (r.messages.length) {
        lastFetchedRef.current = r.messages[r.messages.length - 1].created_at;
      } else if (initial) {
        lastFetchedRef.current = new Date().toISOString();
      }
      onThreadUpdate?.();
    } catch {}
  };

  useEffect(() => {
    refresh(true);
    const t = setInterval(() => refresh(false), POLL_MS);
    return () => clearInterval(t);
  }, [thread.id]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const msg = await marketApi.sendMessage(auth.token, thread.id, text.trim());
      setMsgs((prev) => [...prev, msg]);
      lastFetchedRef.current = msg.created_at;
      setText("");
      onThreadUpdate?.();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error al enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="chat-panel" className="flex flex-col h-[70vh] border hairline bg-cream-card rounded-2xl overflow-hidden">
      <div className="flex items-center gap-3 p-3 border-b hairline bg-cream">
        <button onClick={onBack} className="md:hidden p-1.5 rounded-full hover:bg-cream-deep" data-testid="chat-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        {thread.listing_photo && (
          <img src={thread.listing_photo} alt="" className="w-10 h-10 rounded-lg object-cover" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{thread.counterparty_name}</p>
          <p className="text-[11px] text-ink-mid truncate font-mono">{thread.listing_title}</p>
        </div>
        <Link to={`/marketplace/${thread.listing_id}`} title="Ver anuncio"
              className="p-1.5 rounded-full hover:bg-cream-deep">
          <ExternalLink className="w-4 h-4" />
        </Link>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2 bg-cream">
        {msgs.length === 0 && (
          <p className="text-center text-xs text-ink-mid py-8">Aún no hay mensajes en este hilo.</p>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === auth?.user?.id;
          return (
            <div key={m.id} data-testid={`msg-${m.id}`}
                 className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-snug
                ${mine ? "bg-coral text-white rounded-br-md" : "bg-cream-card border hairline rounded-bl-md"}`}>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[9px] mt-1 ${mine ? "text-white/70" : "text-ink-mid"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="p-3 border-t hairline bg-cream-card flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribe un mensaje…"
          data-testid="chat-input"
          maxLength={2000}
          className="flex-1 bg-cream border hairline rounded-full px-4 py-2 text-sm outline-none focus:border-coral"
        />
        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send"
                className="px-4 py-2 rounded-full bg-coral text-white hover:bg-coral-hover disabled:opacity-40 inline-flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> Enviar
        </button>
      </form>
    </div>
  );
}
